import axios from "axios";

/**
 * BlendedService class to handle blended course-related API calls.
 */
class BlendedService {
  static BASE_URL = process.env.REACT_APP_BASE_URL1;

  /**
   * Saves a new blended course with its modules to the backend.
   * @param {Object} payload - The course data containing courseName and modules
   * @returns {Promise<Object>} The response data from the server
   * @throws Will throw an error if the request fails.
   */
  static async saveCourse(payload) {
    try {
      const response = await axios.post(
        `${BlendedService.BASE_URL}/admin/api/blended-courses/create-course`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response || error.message);
      throw new Error(
        error.response?.data?.message || "Failed to communicate with the server."
      );
    }
  }

  /**
   * Fetches all blended courses from the backend.
   * @returns {Promise<Array>} The list of all blended courses
   * @throws Will throw an error if the request fails.
   */
  static async getAllBlendedCourses(track) {
    try {
      const response = await axios.get(
        `${BlendedService.BASE_URL}/admin/api/blended-courses/get-all-blended-courses/${track}`
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response || error.message);
      throw new Error(
        error.response?.data?.message || "Failed to communicate with the server."
      );
    }
  }

  /**
   * Fetches a specific blended course by ID.
   * @param {string|number} courseId - The ID of the course to fetch
   * @returns {Promise<Object>} The course data
   * @throws Will throw an error if the request fails.
   */
  static async getCourseById(courseId) {
    try {
      const response = await axios.get(
        `${BlendedService.BASE_URL}/admin/api/blended-courses/get-course/${courseId}`
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response || error.message);
      throw new Error(
        error.response?.data?.message || "Failed to communicate with the server."
      );
    }
  }

  /**
   * Updates an existing blended course.
   * @param {Object} payload - The updated course data
   * @returns {Promise<Object>} The response data from the server
   * @throws Will throw an error if the request fails.
   */
  static async updateCourse(payload) {
    try {
      const response = await axios.put(
        `${BlendedService.BASE_URL}/admin/api/blended-courses/update-course`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response || error.message);
      throw new Error(
        error.response?.data?.message || "Failed to communicate with the server."
      );
    }
  }

  /**
   * Deletes a blended course by ID.
   * @param {string|number} blendedProgramId - The ID of the course to delete (maps to blendedCourseId in backend)
   * @param {string} blendedProgramName - The name of the course to delete
   * @returns {Promise<boolean>} True if deleted successfully, false if not deleted
   */
  static async deleteCourse(blendedProgramId, blendedProgramName) {
    try {
      const response = await axios.delete(
        `${BlendedService.BASE_URL}/admin/api/blended-courses/delete-course/${blendedProgramId}/${encodeURIComponent(blendedProgramName)}`
      );
      return response.status === 204;
    } catch (error) {
      if (error.response?.status === 409) {
        return false; // Course is referenced and cannot be deleted
      }
      throw error;
    }
  }
}

export default BlendedService;
